// Text Editor Types and Utilities

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
  color?: string;
  fill: import('../../types/design').FillSettings;
  letterSpacing: number;
  lineHeight: number;
  textAlign: 'left' | 'center' | 'right' | 'justify';

  // Text styling
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  uppercase: boolean;
  textTransform?: 'none' | 'uppercase' | 'lowercase';

  // Text effects
  textShadow?: string;
  textStroke?: string;
  backgroundColor?: string;
  textEffect?: 'none' | 'shadow' | 'lift' | 'hollow' | 'outline' | 'splice' | 'echo' | 'glitch' | 'neon' | 'background';
  backgroundSpread?: number;
  backgroundCornerRadius?: number;
  backgroundOpacity?: number;
  effectIntensity?: number;
  effectColor?: string;
  effectDirection?: number;
  effectOffset?: number;
  effectThickness?: number;

  // Transform
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;

  animation: import('../../types/design').AnimationSettings;

  // Box settings
  autoResize: boolean;
  wordWrap: boolean;
  verticalTrim: boolean;
  opacity: number;
  verticalAlign?: 'top' | 'center' | 'bottom';
  binding?: {
    sourceType?: 'custom_api' | 'webhook';
    customApiId?: string;
    webhookId?: string;
    apiKey: string;
    fallbackValue?: string;
  };
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'Bricolage Grotesque',
  fontSize: 16,
  fontWeight: 'normal',
  fill: { type: 'solid', color: '#000000' },
  letterSpacing: 0,
  lineHeight: 1.5,
  textAlign: 'left',
  italic: false,
  underline: false,
  strikethrough: false,
  uppercase: false,
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
  animation: {
    type: 'none',
    duration: 1000,
    delay: 0,
    easing: 'ease',
    loop: false,
  },
  autoResize: true,
  wordWrap: true,
  verticalTrim: false,
  opacity: 100,
};

export const FONT_FAMILIES = [
  // --- As seen in image ---
  'Bricolage Grotesque',
  'Inter',
  'Arial',
  'Times New Roman',
  'Lora',
  'Dancing Script',
  'Bebas Neue',
  'Fredoka',
  'Cinzel',
  'Cinzel Decorative',
  'Shrikhand',
  'Pacifico',
  'Oswald',
  'Montserrat',
  'Playfair Display',

  // --- Professional & Modern ---
  'Poppins',
  'Roboto',
  'Open Sans',
  'Outfit',
  'Public Sans',

  // --- Elegant & Serif ---
  'Libre Baskerville',
  'Merriweather',
  'Crimson Pro',
  'Georgia',

  // --- Handwriting ---
  'Caveat',
  'Shadows Into Light',
  'Great Vibes',
  'Handlee',

  // --- Monospaced ---
  'JetBrains Mono',
  'Fira Code',
];

export const FONT_WEIGHTS = [
  { label: 'Normal', value: 'normal' as const },
  { label: 'Medium', value: 'medium' as const },
  { label: 'Semibold', value: 'semibold' as const },
  { label: 'Bold', value: 'bold' as const },
  { label: 'Extrabold', value: 'extrabold' as const },
  { label: 'Black', value: 'black' as const },
];

export function textStyleToCSS(style: TextStyle): React.CSSProperties {
  const fontWeightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  };

  return {
    fontFamily: style.fontFamily,
    fontSize: `${style.fontSize}px`,
    fontWeight: fontWeightMap[style.fontWeight],
    color: style.color,
    letterSpacing: `${style.letterSpacing}px`,
    lineHeight: style.lineHeight,
    textAlign: style.textAlign,
    fontStyle: style.italic ? 'italic' : 'normal',
    textDecoration: [
      style.underline ? 'underline' : '',
      style.strikethrough ? 'line-through' : '',
    ].filter(Boolean).join(' ') || 'none',
    textTransform: style.uppercase ? 'uppercase' : 'none',
    textShadow: style.textShadow,
    WebkitTextStroke: style.textStroke,
    backgroundColor: style.backgroundColor,
    transform: `rotate(${style.rotation}deg) scaleX(${style.flipHorizontal ? -1 : 1}) scaleY(${style.flipVertical ? -1 : 1})`,
    whiteSpace: style.wordWrap ? 'normal' : 'nowrap',
    opacity: style.opacity / 100,
  };
}
